import { DialogType } from '@tableau/widgets';
import React from 'react';

import { ActionBarButton } from '~common/react/components/ActionBar';
import { ActionsMenuItem } from '~common/react/components/SharedWidgetsActionMenu';
import { openReactConfirmationDialog } from '~commonInfra/dialogs/ReactConfirmationDialog';
import { ModalInstance, ReactModalOptions, ReactModalService } from '~commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { getAppStore } from '~src/app/AppStore';
import { Formatters, Messages } from '~src/codegen/Localize';
import { ExtractTasks } from '~src/extract/resources/ExtractTasks';
import { RunExtractTasksAction } from '~src/extract/services/RunExtractTasksAction';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { IPublishedConnectionDetailInfo } from '~src/publishedConnection/models/IPublishedConnectionInfo';
import { currentUserIsOwnerOrAdmin, hasRunNowPermission } from '~src/spacesPlatform/utils/MixedContentHelpers';
import { L10n } from '~src/ts/L10n/L10n';
import { IExtractTaskInfo } from '~src/ts/models/IExtractTaskInfo';
import { Set } from '~src/ts/models/Set';

import { PublishedConnections } from '../../resources/PublishedConnections';
import {
  PublishedConnectionCreateOrEditTaskDialogContent,
  PublishedConnectionCreateOrEditTaskDialogContentProps
} from './PublishedConnectionCreateOrEditTaskDialogContent';
import { PublishedConnectionEditScheduleDialogContent } from './PublishedConnectionEditScheduleDialogContent';

function editScheduleDialog(
  tasks: ReadonlyArray<IExtractTaskInfo>,
  close: () => void,
  dismiss: () => void
): ReactModalOptions {
  return {
    type: DialogType.NormalWithTitleBar,
    title: Messages.changeSubscriptionsFrequency(),
    dialogContent: <PublishedConnectionEditScheduleDialogContent tasks={tasks} close={close} dismiss={dismiss}/>,
    showCloseButton: true,
    dialogWidth: TabDialogWidth.width700px,
    shouldRecenterOnUpdate: true
  };
}

function openEditScheduleDialog(tasks: ReadonlyArray<IExtractTaskInfo>): Promise<any> {
  let modalInstance: ModalInstance;
  const close = () => modalInstance.close();
  const dismiss = () => modalInstance.dismiss();
  modalInstance = ReactModalService.open(editScheduleDialog(tasks, close, dismiss));
  return modalInstance.result;
}

function createOrEditTaskDialog(props: PublishedConnectionCreateOrEditTaskDialogContentProps): ReactModalOptions {
  return {
    type: DialogType.NormalWithTitleBar,
    title: props.taskInfo ? Messages.editTask() : Messages.schedulePublishedConnectionTask(),
    dialogContent: (
      <PublishedConnectionCreateOrEditTaskDialogContent {...props}/>
    ),
    showCloseButton: true,
    dialogWidth: TabDialogWidth.width700px,
    shouldRecenterOnUpdate: true
  };
}

function openCreateTaskDialog(publishedConnectionInfo: IPublishedConnectionDetailInfo): Promise<any> {
  let modalInstance: ModalInstance;
  const close = () => modalInstance.close();
  const dismiss = () => modalInstance.dismiss();
  modalInstance = ReactModalService.open(createOrEditTaskDialog({ close, dismiss, publishedConnectionInfo }));
  return modalInstance.result;
}

function openEditTaskDialog(taskInfo: IExtractTaskInfo, publishedConnectionInfo: IPublishedConnectionDetailInfo): Promise<any> {
  let modalInstance: ModalInstance;
  const close = () => modalInstance.close();
  const dismiss = () => modalInstance.dismiss();
  modalInstance = ReactModalService.open(createOrEditTaskDialog({ close, dismiss, publishedConnectionInfo, taskInfo }));
  return modalInstance.result;
}

function deleteAction(tasks: ReadonlyArray<IExtractTaskInfo>) {
  return ExtractTasks.del(tasks.map(task => task.id));
}

function openDeleteTasksDialog(tasks: ReadonlyArray<IExtractTaskInfo>): Promise<IResult> {
  let description: string;
  if (tasks.length === 1) {
    const task = tasks[0];
    description = Messages.confirmDeletePublishedConnectionTask({ TASK_NAME: L10n.elideIfLong(task.virtualConnection.name) });
  } else {
    description = Messages.confirmDeletePublishedConnectionTasks({
      ITEM_COUNT_LOC: Formatters.formatCount(tasks.length)
    });
  }

  const confirmationDialog = openReactConfirmationDialog<ReadonlyArray<IExtractTaskInfo>, IResult>({
    title: Messages.deleteMessage(),
    message: description,
    paramsForAction: tasks,
    actionName: Messages.confirmDeleteButton({
      ITEM_COUNT: tasks.length,
      ITEM_COUNT_LOC: Formatters.formatCount(tasks.length)
    }),
    isActionDelete: true,
    dialogWidth: TabDialogWidth.width375px,
    action: deleteAction
  });
  return confirmationDialog.result;
}

function editScheduleAction(
  tasks: ReadonlyArray<IExtractTaskInfo>,
  done: () => void
): ActionsMenuItem {
  return {
    label: Messages.changeFrequency_action(),
    execute: () => {
      openEditScheduleDialog(tasks)
        .then(done)
        .catch(() => {
          // don't do anything
        });
    }
  };
}

function editTaskAction(
  task: IExtractTaskInfo,
  publishedConnectionInfo: IPublishedConnectionDetailInfo,
  done: () => void
): ActionsMenuItem {
  return {
    label: Messages.editTask_action(),
    execute: () => {
      openEditTaskDialog(task, publishedConnectionInfo)
        .then(done)
        .catch(() => {
          // don't do anything
        });
    }
  };
}

function canRunNow(publishedConnectionInfo: IPublishedConnectionDetailInfo): boolean {
  const state = getAppStore().getState();
  return currentUserIsOwnerOrAdmin(state, publishedConnectionInfo) && hasRunNowPermission(state);
}

function runNowTaskAction(
  tasks: ReadonlyArray<IExtractTaskInfo>,
  publishedConnectionInfo: IPublishedConnectionDetailInfo,
  done: () => void
): ActionsMenuItem {
  return {
    label: Messages.runNow_action(),
    disabled: !canRunNow(publishedConnectionInfo),
    execute: () => {
      RunExtractTasksAction.execute(Set.fromIdItemArray(tasks))
        .then(done)
        .catch(() => {
          // don't do anything
        });
    }
  };
}


function createTaskAction(
  publishedConnectionInfo: IPublishedConnectionDetailInfo,
  done: () => void
): ActionBarButton {
  return {
    label: Messages.newTask(),
    execute: () => {
      // open create task dialog
      openCreateTaskDialog(publishedConnectionInfo)
        .then(done)
        .catch(() => {
          // don't do anything
        });
    },
  };
}

function deleteTasksAction(
  tasks: ReadonlyArray<IExtractTaskInfo>,
  done: () => void
): ActionsMenuItem {
  return {
    label: Messages.delete_action(),
    execute: () => {
      openDeleteTasksDialog(tasks).then(result => {
        PublishedConnections.toast(result,
          Messages.deletePublishedConnectionTasksSuccess({
            ITEM_COUNT: tasks.length,
            ITEM_COUNT_LOC: Formatters.formatCount(tasks.length)
          }),
          Messages.deletePublishedConnectionTasksError({
            ERROR_COUNT: result.errors.length,
            ERROR_COUNT_LOC: Formatters.formatCount(result.errors.length)
          })
        );
        done();
      }, () => {
        // don't do anything
      });
    }
  };
}

function getTasksActions(
  taskList: ReadonlyArray<IExtractTaskInfo>,
  publishedConnectionInfo: IPublishedConnectionDetailInfo,
  done: () => void
): ReadonlyArray<ActionsMenuItem> {
  const allowedActions = [];

  if (taskList.length === 1) {
    // we just show the edit task dialog which also contains ability to change schedule
    allowedActions.push(editTaskAction(taskList[0], publishedConnectionInfo, done));
  } else {
    // if multiple selected we allow change schedule
    allowedActions.push(editScheduleAction(taskList, done));
  }
  allowedActions.push(runNowTaskAction(taskList, publishedConnectionInfo, done));
  allowedActions.push(deleteTasksAction(taskList, done));

  return allowedActions;
}

export const PublishedConnectionTaskActions = {
  createTaskAction,
  deleteTasksAction,
  editScheduleAction,
  editTaskAction,
  getTasksActions,
  runNowTaskAction
};
